import { zodResolver } from '@hookform/resolvers/zod';
import {
  IInvoiceCustomizationSettings,
  InvoiceContactEditBehaviorEnum,
  InvoiceCustomizationSettingsSchema,
  ITaxConnectionBaseSchema,
  TaxStatusEnum,
} from '@monetize/types/app';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../../api/axios';
import { useGetListData } from '../../../../api/queryUtils';
import {
  MAccordion,
  MAlert,
  MButton,
  MFlex,
  MFormField,
  MFormFieldProps,
  MGrid,
  MGridItem,
  MStack,
  MText,
  RadioStyleButtonGroup,
} from '../../../../components/Monetize';
import { MSettingAccordionItem } from '../../../../components/Monetize/MSettingAccordionItem';
import { useAccordionControl } from '../../../../hooks';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../../hooks/useSettingsSectionHighlight';

export type InvoiceContactSettingsProps = {
  invoiceCustomizationSettings: IInvoiceCustomizationSettings | undefined;
  onSubmit: (data: IInvoiceCustomizationSettings) => Promise<void>;
};

const CONTACT_EDIT_BEHAVIOR_DESCRIPTIONS: Record<
  InvoiceContactEditBehaviorEnum,
  string
> = {
  [InvoiceContactEditBehaviorEnum.ENABLED]:
    'You can edit contacts without requiring a credit and re-bill.',
  [InvoiceContactEditBehaviorEnum.ENABLED_IF_PERIOD_OPEN]:
    'If the finance period is open, you can edit contacts without requiring a credit and re-bill.',
  [InvoiceContactEditBehaviorEnum.DISABLED]:
    'All contact edits will require a credit and re-bill.',
};

const fieldProps: MFormFieldProps = {
  labelProps: { fontWeight: 'normal' },
  borderBottomWidth: '1px',
  paddingBottom: 2,
  borderColor: 'border.subtle',
};

export const InvoiceContactSettings = ({
  invoiceCustomizationSettings,
  onSubmit,
}: InvoiceContactSettingsProps) => {
  const isHighlighted = useSettingsSectionHighlight('invoiceContactSettings');
  const [isLoading, setIsLoading] = useState(false);
  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  useEffect(() => {
    if (isHighlighted) {
      setAccordionValue(['0']);
    }
  }, [isHighlighted, setAccordionValue]);

  const { data: isTaxProviderConnected } = useGetListData<
    ITaxConnectionBaseSchema,
    boolean
  >('tax', undefined, {
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
    select: (data) => {
      const taxProvider = data as unknown as ITaxConnectionBaseSchema[];
      return taxProvider?.some(({ status }) => status === TaxStatusEnum.ACTIVE);
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    reset,
  } = useForm<IInvoiceCustomizationSettings>({
    resolver: zodResolver(InvoiceCustomizationSettingsSchema),
    mode: 'onChange',
    defaultValues: {
      invoiceContactEditBehavior: InvoiceContactEditBehaviorEnum.DISABLED,
    },
  });

  useEffect(() => {
    if (invoiceCustomizationSettings) {
      reset(invoiceCustomizationSettings);
    }
  }, [invoiceCustomizationSettings, reset]);

  const handleSubmitContactSettings = async (
    data: IInvoiceCustomizationSettings,
  ) => {
    try {
      setIsLoading(true);
      await onSubmit(data);
    } catch (err: any) {
      handleApiErrorToast(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MAccordion
      collapsible
      w="100%"
      multiple
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
    >
      <MSettingAccordionItem
        value="0"
        id="invoiceContactSettings"
        description="Configure contact editing behavior for unpaid invoices."
        label="Invoice Contact Settings"
        {...getHighlightStyles(isHighlighted)}
        borderWidth="1px"
      >
        <form onSubmit={handleSubmit(handleSubmitContactSettings)}>
          {isTaxProviderConnected && (
            <MAlert type="warning" mb={4}>
              If you have a tax provider configured, changing the shipping
              contact will always require a credit and re-bill regardless of the
              setting below.
            </MAlert>
          )}
          <MGrid templateColumns="repeat(12, 1fr)" gap={2}>
            <MGridItem colSpan={12}>
              <MStack>
                <MFormField
                  label="Contact editing behavior"
                  error={errors.invoiceContactEditBehavior}
                  {...fieldProps}
                >
                  <Controller
                    name="invoiceContactEditBehavior"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <MStack gap={2} w="full">
                        <RadioStyleButtonGroup
                          name="invoiceContactEditBehavior"
                          value={value}
                          isDisabled={isLoading}
                          onChange={onChange}
                          options={[
                            {
                              label: 'Enabled',
                              value: InvoiceContactEditBehaviorEnum.ENABLED,
                            },
                            {
                              label: 'Enabled if Period Open',
                              value:
                                InvoiceContactEditBehaviorEnum.ENABLED_IF_PERIOD_OPEN,
                            },
                            {
                              label: 'Disabled',
                              value: InvoiceContactEditBehaviorEnum.DISABLED,
                            },
                          ]}
                        />
                        {value && (
                          <MText fontSize="sm" color="tGray.darkGrayPurple">
                            {CONTACT_EDIT_BEHAVIOR_DESCRIPTIONS[value]}
                          </MText>
                        )}
                      </MStack>
                    )}
                  />
                </MFormField>
              </MStack>
            </MGridItem>
          </MGrid>
          <MFlex justifyContent="flex-end" mt={4}>
            <MButton
              type="submit"
              variant="primary"
              alignSelf="self-end"
              isDisabled={isLoading || !isDirty || !isValid}
              isLoading={isLoading}
              onClick={handleSubmit(handleSubmitContactSettings)}
            >
              Save
            </MButton>
          </MFlex>
        </form>
      </MSettingAccordionItem>
    </MAccordion>
  );
};
